// Ported from obsidian-plugin/src/components/SourceList.tsx.
// Obsidian's app.workspace.openLinkText() is replaced with react-router navigation
// to /sources/:citeKey.
import { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { SourceDoc } from '../../types';
import { SummaryPopover } from './SummaryPopover';
import { groupSourceDocs } from '../../lib/sourceDocs';

interface SourceListProps {
  sources: SourceDoc[];
}

function isWebSource(src: SourceDoc): boolean {
  return src.cite_key.startsWith('web:') || src.retrieved_by === 'web_search';
}

function webChipLabel(src: SourceDoc): string {
  // Strip "web:" prefix to show just the domain
  return src.cite_key.startsWith('web:') ? src.cite_key.slice(4) : src.cite_key;
}

export function SourceList({ sources }: SourceListProps) {
  const navigate = useNavigate();
  const [hovered, setHovered] = useState<{ citeKey: string; el: HTMLElement } | null>(null);
  const enterTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const graceTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  if (!sources.length) return null;

  const openSource = (src: SourceDoc) => {
    if (isWebSource(src) && src.url) {
      window.open(src.url, '_blank', 'noopener,noreferrer');
    } else {
      navigate(`/sources/${encodeURIComponent(src.cite_key)}`);
    }
  };

  const clearTimers = () => {
    if (enterTimeout.current) {
      clearTimeout(enterTimeout.current);
      enterTimeout.current = null;
    }
    if (graceTimeout.current) {
      clearTimeout(graceTimeout.current);
      graceTimeout.current = null;
    }
  };

  const handleChipEnter = (citeKey: string, el: HTMLElement) => {
    // Don't show popover for web sources (they have no summary)
    if (citeKey.startsWith('web:')) return;

    clearTimers();
    enterTimeout.current = setTimeout(() => {
      setHovered({ citeKey, el });
    }, 300);
  };

  const handleChipLeave = () => {
    if (enterTimeout.current) {
      clearTimeout(enterTimeout.current);
      enterTimeout.current = null;
    }
    // Grace period: let user move mouse into the popover
    graceTimeout.current = setTimeout(() => {
      setHovered(null);
    }, 150);
  };

  const handlePopoverEnter = useCallback(() => {
    if (graceTimeout.current) {
      clearTimeout(graceTimeout.current);
      graceTimeout.current = null;
    }
  }, []);

  const handlePopoverLeave = useCallback(() => {
    setHovered(null);
  }, []);

  const handlePopoverClose = useCallback(() => {
    clearTimers();
    setHovered(null);
  }, []);

  return (
    <div className="malpedia-source-list">
      {groupSourceDocs(sources).map(({ doc: src, summaryOnly }) => {
        const web = isWebSource(src);
        const isHovered = hovered?.citeKey === src.cite_key;
        const label = src.url ? `${src.title || src.cite_key} — ${src.url}` : src.title || src.cite_key;
        return (
          <span
            key={src.cite_key}
            className={`malpedia-source-chip${web ? ' malpedia-source-chip--web' : ''}${summaryOnly ? ' malpedia-source-chip--summary' : ''}`}
            title={
              isHovered
                ? undefined
                : summaryOnly
                  ? `${label}\nSeen as a summary card, not read in full`
                  : label
            }
            onClick={() => openSource(src)}
            onMouseEnter={(e) => handleChipEnter(src.cite_key, e.currentTarget)}
            onMouseLeave={handleChipLeave}
          >
            {web ? webChipLabel(src) : src.cite_key}
          </span>
        );
      })}
      {hovered && (
        <SummaryPopover
          targetEl={hovered.el}
          citeKey={hovered.citeKey}
          onClose={handlePopoverClose}
          onMouseEnter={handlePopoverEnter}
          onMouseLeave={handlePopoverLeave}
        />
      )}
    </div>
  );
}
